/**
 * First-touch ad attribution, stored in a first-party cookie (`ab_attrib`).
 *
 * A Google Ads click lands on e.g. `/wcag-audit?gclid=…&utm_source=google`. The
 * visitor may browse to other pages, leave and come back days later before
 * filling a form, so the click IDs and UTMs are saved on arrival and sent with
 * whichever lead form they eventually submit. The CRM's Google Ads export
 * (offline conversions) needs the gclid; the rest is for reporting.
 *
 * Rules:
 *  - A visit that carries ad parameters replaces the stored ones (latest click
 *    wins — that is the click Google Ads attributes the conversion to).
 *  - A visit WITHOUT ad parameters never wipes what is stored, so a later plain
 *    visit to "/" keeps the gclid (QA Q4).
 *  - An empty `gclid=` never overwrites a stored gclid.
 *
 * Every function is SSR-safe: at pre-render time there is no `document`, and
 * they return empty values instead of throwing.
 */

export const ATTRIBUTION_COOKIE = "ab_attrib";
/** Google Ads' click-through conversion window, so we keep the click as long. */
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

/** URL query keys captured on arrival. */
const URL_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "matchtype",
  "service",
] as const;
/** The keys that mark a visit as coming from an ad / tagged campaign. */
const CAMPAIGN_KEYS = URL_KEYS.filter((k) => k !== "service");
/** Matches the backend's per-field cap (gclid is 255; the rest 200). */
const MAX_LEN = 255;

export type AttributionKey = (typeof URL_KEYS)[number];

export type Attribution = Partial<Record<AttributionKey, string>> & {
  /** Path of the page the visitor first landed on (no query string). */
  landing_page?: string;
  /** ISO-8601 time of that first landing. */
  first_touch_ts?: string;
};

function hasDocument(): boolean {
  return typeof document !== "undefined";
}

/** The stored attribution, or `{}` when there is none (or it is unreadable). */
export function getAttribution(): Attribution {
  if (!hasDocument()) return {};
  const raw = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${ATTRIBUTION_COOKIE}=`))
    ?.slice(ATTRIBUTION_COOKIE.length + 1);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(raw));
    return parsed && typeof parsed === "object" ? (parsed as Attribution) : {};
  } catch {
    return {};
  }
}

function writeAttribution(a: Attribution): void {
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(a))}` +
    `; Max-Age=${MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

/**
 * Read ad parameters from the current URL into the cookie. Call once on app
 * start (main.tsx). `search` / `pathname` / `now` are injectable for tests.
 */
export function captureAttribution(
  search: string = hasDocument() ? location.search : "",
  pathname: string = hasDocument() ? location.pathname : "/",
  now: Date = new Date(),
): Attribution {
  if (!hasDocument()) return {};
  const params = new URLSearchParams(search);
  const fromUrl: Attribution = {};
  for (const key of URL_KEYS) {
    const value = params.get(key)?.trim();
    if (value) fromUrl[key] = value.slice(0, MAX_LEN);
  }

  const stored = getAttribution();
  const isCampaignVisit = CAMPAIGN_KEYS.some((k) => fromUrl[k]);

  let next: Attribution;
  if (isCampaignVisit) {
    // A new ad click: start a fresh touch, but carry a stored gclid over when
    // this URL has none (e.g. a utm-only link clicked later).
    next = {
      ...fromUrl,
      gclid: fromUrl.gclid ?? stored.gclid,
      landing_page: pathname.slice(0, 200),
      first_touch_ts: now.toISOString(),
    };
  } else if (fromUrl.service && Object.keys(stored).length) {
    // Same visitor switching service variant: remember it, keep the touch.
    next = { ...stored, service: fromUrl.service };
  } else {
    // No ad parameters: nothing to record (organic visitors get no cookie),
    // and whatever an earlier ad click stored is left as it is.
    return stored;
  }

  // Drop undefined keys so the cookie stays small.
  for (const k of Object.keys(next) as (keyof Attribution)[]) {
    if (next[k] === undefined) delete next[k];
  }
  writeAttribution(next);
  return next;
}
